import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import LandmarkGlobalLogo from "@redotech/redo-icons/landmark-global.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import {
  ChannelConnectionState,
  SettingsCard,
} from "@redotech/redo-web/arbiter-components/settings-card/settings-card";
import { Flex } from "@redotech/redo-web/flex";
import { groupInput, input } from "@redotech/ui/form";
import { Fragment, memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";

const redoLandmarkGlobalForm = groupInput({
  username: input<string>(),
  password: input<string>(),
  shippingLaneRegion: input<string>(),
  clientId: input<string>(),
});

interface RedoLandmarkGlobalIntegrationProps {
  connectionState: ChannelConnectionState;
  setConnectionState: (state: ChannelConnectionState) => void;
}

export const RedoLandmarkGlobalIntegration = memo(
  function RedoLandmarkGlobalIntegration({
    connectionState,
    setConnectionState,
  }: RedoLandmarkGlobalIntegrationProps) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const team = useRequiredContext(TeamContext);
    const [modalOpen, setModalOpen] = useState(false);
    const formInput = useInput(redoLandmarkGlobalForm, {
      username: "",
      password: "",
      shippingLaneRegion: "",
      clientId: "",
    });

    const [createIntegrationLoad, triggerCreateIntegration] = useTriggerLoad(
      async (signal) => {
        await rpcClient.createRedoLandmarkGlobalIntegration(
          {
            teamId: team._id,
            username: formInput.value.username,
            password: formInput.value.password,
            shippingLaneRegion: formInput.value.shippingLaneRegion,
            clientId: formInput.value.clientId,
          },
          { signal },
        );
        setConnectionState(ChannelConnectionState.CONNECTED);
        setModalOpen(false);
      },
    );

    const onConnect = useHandler(async () => {
      triggerCreateIntegration();
    });

    const onOpenModal = useHandler(() => {
      setModalOpen(true);
    });

    const onCloseModal = useHandler(() => {
      setModalOpen(false);
    });

    return (
      <Fragment>
        <SettingsCard
          buttonSection={
            connectionState === ChannelConnectionState.NOT_CONNECTED ? (
              <RedoButton onClick={onOpenModal} size="xs" text="Connect" />
            ) : undefined
          }
          cardWidth="full"
          connectionState={connectionState}
          customBorderRadius={6}
          description="Purchase labels through Redo Landmark Global."
          Logo={LandmarkGlobalLogo}
          name="Redo Landmark Global"
        />

        <RedoModal
          isOpen={modalOpen}
          onModalCloseRequested={onCloseModal}
          primaryButton={{
            text: "Connect",
            onClick: onConnect,
            disabled:
              !formInput.value.username ||
              !formInput.value.password ||
              !formInput.value.shippingLaneRegion ||
              !formInput.value.clientId,
            pending: createIntegrationLoad.pending,
          }}
          secondaryButton={{ text: "Cancel", onClick: onCloseModal }}
          title="Connect Redo Landmark Global"
        >
          <Flex dir="column" gap="xl">
            <RedoTextInput
              label="Username"
              setValue={formInput.inputs.username.setValue}
              value={formInput.inputs.username.value}
            />

            <RedoTextInput
              inputTypeHint="password"
              label="Password"
              setValue={formInput.inputs.password.setValue}
              value={formInput.inputs.password.value}
            />

            <RedoTextInput
              label="Shipping lane region"
              setValue={formInput.inputs.shippingLaneRegion.setValue}
              value={formInput.inputs.shippingLaneRegion.value}
            />

            <RedoTextInput
              label="Client ID"
              setValue={formInput.inputs.clientId.setValue}
              value={formInput.inputs.clientId.value}
            />
          </Flex>
        </RedoModal>
      </Fragment>
    );
  },
);
